import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Gift } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [loading, setLoading] = useState(false);

  // Capture referral code from URL or localStorage (set by ProductPage CTAs etc.)
  useEffect(() => {
    const fromUrl = searchParams.get("ref");
    const stored = localStorage.getItem("pending_referral_code");
    const code = (fromUrl || stored || "").trim().toUpperCase();
    if (code) {
      setReferralCode(code);
      if (fromUrl) localStorage.setItem("pending_referral_code", code);
    }
  }, [searchParams]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error(t("auth.password_too_short"));
      return;
    }
    setLoading(true);
    try {
      await register(name, email, password, referralCode || null);
      if (referralCode) {
        localStorage.removeItem("pending_referral_code");
        toast.success(t("referral.welcome_filleul", { code: referralCode }));
      } else {
        toast.success(t("auth.account_created"));
      }
      navigate("/account");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("auth.register_error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-24" data-testid="register-page">
      <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">{t("auth.welcome")}</div>
      <h1 className="font-display mt-3 text-4xl font-bold uppercase tracking-tighter text-white">{t("auth.register_title")}</h1>

      <form onSubmit={submit} className="mt-10 space-y-6" data-testid="register-form">
        {referralCode && (
          <div
            className="flex items-center gap-3 border border-[#FFD700]/40 bg-gradient-to-r from-[#FFD700]/10 to-transparent px-4 py-3"
            data-testid="register-referral-banner"
          >
            <Gift size={20} strokeWidth={1.5} className="text-[#FFD700] flex-shrink-0" />
            <div className="leading-tight">
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#FFD700]">
                {t("referral.banner_title")}
              </div>
              <div className="text-xs text-white/80 mt-0.5">
                {t("referral.banner_subtitle", { code: referralCode })}
              </div>
            </div>
          </div>
        )}
        <label className="block">
          <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#C5CDD8]">{t("auth.name")}</span>
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-2 w-full border-b border-white/40 bg-transparent text-white px-0 py-2 text-sm focus:outline-none focus:border-white"
            data-testid="register-name"
          />
        </label>
        <label className="block">
          <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#C5CDD8]">{t("auth.email")}</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-2 w-full border-b border-white/40 bg-transparent text-white px-0 py-2 text-sm focus:outline-none focus:border-white"
            data-testid="register-email"
          />
        </label>
        <label className="block">
          <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#C5CDD8]">{t("auth.password")}</span>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-2 w-full border-b border-white/40 bg-transparent text-white px-0 py-2 text-sm focus:outline-none focus:border-white"
            data-testid="register-password"
          />
        </label>
        <label className="block">
          <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#C5CDD8]">
            {t("referral.code_field_label")}
          </span>
          <input
            type="text"
            value={referralCode}
            onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
            placeholder="NS-XXXXXX"
            className="mt-2 w-full border-b border-white/40 bg-transparent text-white px-0 py-2 text-sm focus:outline-none focus:border-white tracking-widest"
            data-testid="register-referral-code"
          />
        </label>
        <button
          type="submit"
          disabled={loading}
          className="w-full border border-white bg-white text-[#001F3F] hover:bg-transparent hover:text-white transition-all uppercase tracking-[0.25em] text-xs font-bold py-4 disabled:opacity-60"
          data-testid="register-submit"
        >
          {loading ? t("auth.creating") : t("auth.submit_register")}
        </button>
        <p className="text-sm text-center text-white/60">
          {t("auth.have_account")}{" "}
          <Link to="/login" className="text-white font-bold underline">
            {t("auth.login_link")}
          </Link>
        </p>
      </form>
    </div>
  );
}
